// 3rd party
import * as React from 'react';
import { inject, observer } from 'mobx-react';

// Bernie
import { AnalyticsStore, ContextStore } from 'bernie-core';

// Shared UI
import { LocalizedComponentProps, LocalizedText, withLocalization } from '@shared-ui/localization-context';

// UI Toolkit
import { UitkSecondaryButton } from 'uitk-react-button';
import { UitkIcon } from 'uitk-react-icons';
import { UitkTextArea } from 'uitk-react-input';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLink } from 'uitk-react-link';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkParagraph } from 'uitk-react-text';

// Models
import { Program, Product } from 'models';

// Stores
import { UserAuthStore } from 'stores';

// Utils
import { getProgramAnalyticsMicroMessage, getProductAnalyticsMicroMessage } from '../../util/analytics';

interface SearchProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  userAuth?: UserAuthStore;
  program?: Program;
  product?: Product;
  userProgram?: any;
}

@withLocalization
@inject('context', 'userAuth', 'analytics')
@observer
class Search extends React.Component<SearchProps, any> {
  private copyToClipboardLabel = this.props.l10n.formatText('product.search.code.actions.copy_to_clipboard');
  private copiedToClipboardLabel = this.props.l10n.formatText('product.search.code.actions.copied_to_clipboard');

  public pasteToClipboard(copyText) {
    navigator.clipboard.writeText(copyText);

    const copyButton = document.getElementById('clipboardCopyBtn');
    copyButton.textContent = this.copiedToClipboardLabel;
    setTimeout(() => {
      copyButton.textContent = this.copyToClipboardLabel;
    }, 1500);
  }

  private analyticsTrackEvents = (rfrr: string) => {
    const { analytics, program, product, userProgram } = this.props;

    if (typeof analytics?.trackEvent === 'function' && rfrr === 'affhub.product_config') {
      analytics.trackEvent('affhub.product_config', 'AffHub Product Config Presented', null, null, true, [
        getProgramAnalyticsMicroMessage(program, userProgram.programStatus),
        getProductAnalyticsMicroMessage(product),
      ]);
    } else if (typeof analytics?.trackEvent === 'function' && rfrr === 'affhub_copy_code.selected') {
      analytics.trackEvent(rfrr, 'AffHub Copy Code Selected', null, null, true, [
        getProgramAnalyticsMicroMessage(program, userProgram.programStatus),
        getProductAnalyticsMicroMessage(product),
      ]);
    }
  };

  private renderDocumentationLink() {
    return (
      <UitkSpacing margin={{ blockend: 'six' }}>
        <UitkParagraph size={1}>
          <UitkLink inline={true}>
            <a href={this.props.product.guideLink} target="_blank">
              <LocalizedText message="product.search.code.implementation_guide" />
              <UitkIcon name="open_in_new" />
            </a>
          </UitkLink>
        </UitkParagraph>
      </UitkSpacing>
    );
  }

  private getUserCamRef = (placeholderCamRef: string = '') => {
    return this.props.userProgram.programReference || placeholderCamRef;
  };

  private getIframeSrc = ({ placeholderCamRef }: { placeholderCamRef?: string } = {}): string => {
    return `${this.props.product.demoLink}&camref=${this.getUserCamRef(placeholderCamRef)}`;
  };

  private renderCodeMarkup() {
    const { l10n } = this.props;

    const codeMarkup =
      '<iframe' +
      ` src="${this.getIframeSrc({
        placeholderCamRef: l10n.formatText('product.search.code.insert_ref_here', ['CAMREF']).toUpperCase(),
      })}"` +
      ' width="100%"' +
      ' height="344px"' +
      '></iframe>';

    return (
      <UitkTextArea
        id="searchWidgetCode"
        label={l10n.formatText('product.search.code.form.markup.label')}
        value={codeMarkup}
        hasVisibleLabel={false}
      />
    );
  }

  private renderPreviewFrame() {
    return <iframe src={this.getIframeSrc()} width="100%" height="344px" />;
  }

  public componentDidMount() {
    this.analyticsTrackEvents('affhub.product_config');
  }

  public render() {
    return (
      <main className="container">
        <UitkLayoutGrid columns={{ large: 2 }} space="twelve">
          <UitkLayoutGridItem>
            <div>
              <UitkHeading tag="h4" size={4}>
                <LocalizedText message="product.search.code.title" />
              </UitkHeading>

              <UitkParagraph size={1}>
                <LocalizedText message="product.search.code.instructions" />
              </UitkParagraph>
              {this.renderDocumentationLink()}

              {this.renderCodeMarkup()}
              <UitkParagraph size={1}>
                <UitkSecondaryButton
                  id="clipboardCopyBtn"
                  onClick={() => {
                    const textArea = document.getElementById('searchWidgetCode') as HTMLTextAreaElement;
                    textArea.focus();
                    this.pasteToClipboard(textArea.value);
                    this.analyticsTrackEvents('affhub_copy_code.selected');
                  }}
                >
                  {this.copyToClipboardLabel}
                </UitkSecondaryButton>
              </UitkParagraph>
            </div>
          </UitkLayoutGridItem>

          <UitkLayoutGridItem>
            <div>
              <UitkHeading tag="h4" size={4}>
                <LocalizedText message="product.search.preview.title" />
              </UitkHeading>

              <UitkSpacing margin={{ blockend: 'six' }}>
                <UitkParagraph size={1}>
                  <LocalizedText message="product.search.preview.information" />
                </UitkParagraph>
              </UitkSpacing>

              {this.renderPreviewFrame()}
            </div>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      </main>
    );
  }
}

export { Search };
